/**
 * ViewSelector - Searchable dropdown for selecting views
 *
 * Provides a searchable interface for selecting existing views. Used as a JOIN source
 * selector in JoinBuilder and as a view picker in view_name rules.
 * Filters out views that would create circular dependencies and shows view metadata.
 *
 * Supports two prop styles for controlled component behavior:
 *   - selectedViewId / onSelect (original, used by JoinBuilder)
 *   - value / onChange (idiomatic alias for broader reuse)
 */

import React, { useState, useMemo } from 'react';
import { Eye, Search, AlertCircle, FileText } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { useViews } from '@/hooks/useViews';
import type { View, ViewListItem } from '@/types/views';

export interface ViewSelectorProps {
  /** View ID of the currently selected view (original prop name) */
  selectedViewId?: string;
  /** Callback when a view is selected (original prop name) */
  onSelect?: (view: View | ViewListItem) => void;
  /** Alias for selectedViewId -- idiomatic controlled-component prop */
  value?: string;
  /** Alias for onSelect -- idiomatic controlled-component prop */
  onChange?: (view: View | ViewListItem) => void;
  /** View IDs to exclude from the dropdown (e.g. to prevent circular deps) */
  excludeViewIds?: string[];
  /** View names to exclude from the dropdown (e.g. to prevent self-reference) */
  excludeViewNames?: string[];
  showDependencyWarnings?: boolean;
  disabled?: boolean;
}

export const ViewSelector: React.FC<ViewSelectorProps> = ({
  selectedViewId,
  onSelect,
  value,
  onChange,
  excludeViewIds = [],
  excludeViewNames = [],
  showDependencyWarnings = true,
  disabled = false,
}) => {
  // Resolve aliased props -- value/onChange take precedence if both are provided
  const resolvedViewId = value ?? selectedViewId;
  const resolvedOnSelect = onChange ?? onSelect;
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all views with active status
  const { data: viewsResponse, isLoading, error } = useViews({
    status: 'active',
    size: 100, // Get more views for selection
  });

  const views = viewsResponse?.items || [];

  // Filter views based on search query and exclusions (by ID and by name)
  const filteredViews = useMemo(() => {
    const excludedNamesLower = excludeViewNames.map((n) => n.toLowerCase());

    let filtered = views.filter(
      (view) =>
        !excludeViewIds.includes(view.id) &&
        !excludedNamesLower.includes(view.name.toLowerCase())
    );

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (view) =>
          view.name.toLowerCase().includes(query) ||
          view.description?.toLowerCase().includes(query) ||
          view.tags?.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [views, searchQuery, excludeViewIds, excludeViewNames]);

  // Get the currently selected view for display
  const selectedView = useMemo(() => {
    if (!resolvedViewId) return null;
    return views.find((v) => v.id === resolvedViewId);
  }, [resolvedViewId, views]);

  const handleSelectChange = (viewId: string) => {
    const view = views.find((v) => v.id === viewId);
    if (view && resolvedOnSelect) {
      resolvedOnSelect(view);
    }
  };

  const excludedCount = excludeViewIds.length + excludeViewNames.length;

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label htmlFor="view-selector">Select View</Label>

        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search views by name, description, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={disabled || isLoading}
            className="pl-10"
            aria-label="Search views"
          />
        </div>

        {/* View selector dropdown */}
        <Select
          value={resolvedViewId || ''}
          onValueChange={handleSelectChange}
          disabled={disabled || isLoading}
        >
          <SelectTrigger
            id="view-selector"
            aria-label="Select a view to join"
            className="w-full"
          >
            <SelectValue placeholder={isLoading ? 'Loading views...' : 'Choose a view to JOIN'}>
              {selectedView && (
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedView.name}</span>
                  {selectedView.view_type === 'materialized' && (
                    <Badge variant="secondary" className="text-xs">
                      Materialized
                    </Badge>
                  )}
                </div>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {isLoading && (
              <SelectItem value="__loading__" disabled>
                Loading views...
              </SelectItem>
            )}

            {!isLoading && filteredViews.length === 0 && searchQuery && (
              <SelectItem value="__no_results__" disabled>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Search className="h-4 w-4" />
                  No views found matching &quot;{searchQuery}&quot;
                </div>
              </SelectItem>
            )}

            {!isLoading && filteredViews.length === 0 && !searchQuery && (
              <SelectItem value="__no_views__" disabled>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <AlertCircle className="h-4 w-4" />
                  No available views to join
                </div>
              </SelectItem>
            )}

            {!isLoading &&
              filteredViews.map((view) => (
                <SelectItem key={view.id} value={view.id}>
                  <div className="flex flex-col gap-1 py-1">
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{view.name}</span>
                      {view.view_type === 'materialized' && (
                        <Badge variant="secondary" className="text-xs">
                          Materialized
                        </Badge>
                      )}
                    </div>
                    {view.description && (
                      <span className="text-xs text-muted-foreground line-clamp-1">
                        {view.description}
                      </span>
                    )}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {view.file_count} {view.file_count === 1 ? 'file' : 'files'}
                      </span>
                      {view.row_count !== undefined && view.row_count !== null && (
                        <span>
                          {view.row_count.toLocaleString()}{' '}
                          {view.row_count === 1 ? 'row' : 'rows'}
                        </span>
                      )}
                    </div>
                    {view.tags && view.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {view.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs px-1.5 py-0">
                            {tag}
                          </Badge>
                        ))}
                        {view.tags.length > 3 && (
                          <span className="text-xs text-muted-foreground">
                            +{view.tags.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {/* Dependency warnings */}
      {showDependencyWarnings && excludedCount > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            {excludedCount} {excludedCount === 1 ? 'view is' : 'views are'} excluded to prevent
            circular dependencies
          </AlertDescription>
        </Alert>
      )}

      {/* Error state */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            Failed to load views. Please try again.
          </AlertDescription>
        </Alert>
      )}

      {/* Selected view info */}
      {selectedView && (
        <div className="rounded-lg border bg-muted/50 p-3 space-y-2">
          <div className="flex items-start justify-between">
            <div className="space-y-1 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{selectedView.name}</span>
                <Badge variant={selectedView.view_type === 'materialized' ? 'default' : 'secondary'}>
                  {selectedView.view_type}
                </Badge>
              </div>
              {selectedView.description && (
                <p className="text-xs text-muted-foreground">{selectedView.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {selectedView.file_count} {selectedView.file_count === 1 ? 'file' : 'files'}
            </span>
            {selectedView.row_count !== undefined && selectedView.row_count !== null && (
              <span>{selectedView.row_count.toLocaleString()} rows</span>
            )}
            {selectedView.is_auto_refresh && (
              <Badge variant="outline" className="text-xs">
                Auto-refresh
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Helper text */}
      {!resolvedViewId && !isLoading && filteredViews.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Select a view to join with the current view. The selected view&apos;s columns will be
          available for join conditions.
        </p>
      )}
    </div>
  );
};
